import React from 'react'
import { Work } from '../../../types'
import { WILL_OR_HAS_WORKED_STATUSES } from 'lib/work'
import { isAfter, isPast } from 'date-fns'
import { useShiftDetailContextValue } from 'pages/Shifts/ShiftDetail/context/ShiftDetailProvider'
import { DAYFORCE_SHIFT_TYPES_ACTIVE } from 'pages/Dayforce/constants'
import { Flex, Text, useAppTheme } from '@workwhile/ui'
import { useDayforceJobContextValue } from 'pages/Shifts/ShiftDetail/context/DayforceJobProvider'
import { CreateWorkerSubmissionRequestButton } from './CreateWorkerSubmissionRequestButton'
import { ButtonWithConfirmation } from 'pages/Dayforce/shared/components/ButtonWithConfirmation'
import { CreateContestedHoursRequestButton } from './CreateContestedHoursRequestButton'
import { InfoTable } from 'components/InfoTable'
import styled from 'styled-components'

const StyledInfoTable = styled(InfoTable)`
  td {
    vertical-align: text-top;
  }
`

export const DayforceActions = ({ work }: { work: Work }) => {
  const { space } = useAppTheme()
  const { state: shiftDetail } = useShiftDetailContextValue()
  const {
    state: {
      booking,
      scheduleWorkItemUpdateMutationState,
      cancelContestedHoursRequestMutationState,
    },
    actions: { scheduleWorkItemUpdate, cancelContestedHoursRequest },
  } = useDayforceJobContextValue()

  if (!work?.id || !booking) {
    return null
  }

  let dayforceWorker = booking.workers.find(
    (worker) =>
      worker.userId === Number(work.worker?.id ?? 0) &&
      DAYFORCE_SHIFT_TYPES_ACTIVE.includes(worker.shiftType)
  )
  if (!dayforceWorker) {
    dayforceWorker = booking?.workers.find(
      (worker) => worker.userId === Number(work.worker?.id ?? 0)
    )
  }

  const workerWillOrHasWorked = WILL_OR_HAS_WORKED_STATUSES.includes(
    work?.status ?? 'unknown'
  )

  const shiftHasStarted = isAfter(new Date(), shiftDetail.startsAt)
  const isShiftInThePast = isPast(shiftDetail.endsAt)
  const isWorkerAssignedInDayforce =
    dayforceWorker &&
    DAYFORCE_SHIFT_TYPES_ACTIVE.includes(dayforceWorker.shiftType)

  const showWorkerSubmissionButton =
    workerWillOrHasWorked && !isWorkerAssignedInDayforce && isShiftInThePast

  const initialRequestedBreakDuration =
    shiftDetail.lunchBreak?.type === 'unpaid'
      ? (shiftDetail.lunchBreak?.durationInMinutes ?? 0) / 60
      : null

  const hasPendingContestedHoursRequest =
    dayforceWorker?.pendingContestedHoursRequest
  const canContestHours =
    isShiftInThePast &&
    isWorkerAssignedInDayforce &&
    !hasPendingContestedHoursRequest

  return (
    <StyledInfoTable
      style={{
        maxWidth: 'fit-content',
        tableLayout: 'auto',
        borderSpacing: `0 ${space[2]}`,
        display: 'inline-block',
      }}
    >
      <Text fontWeight="bold">Dayforce actions</Text>
      <Flex gap={2} flexDirection="column" justifyContent="flex-end">
        {workerWillOrHasWorked &&
        !isWorkerAssignedInDayforce &&
        shiftHasStarted &&
        !isShiftInThePast ? (
          <Text>Wait until shift ends to assign worker</Text>
        ) : null}
        {showWorkerSubmissionButton ? (
          <CreateWorkerSubmissionRequestButton associationId={work.id} />
        ) : null}
        {hasPendingContestedHoursRequest ? (
          <ButtonWithConfirmation
            message="Are you sure you want to cancel this contested hours request?"
            variant="secondary"
            kind="small"
            onClick={() =>
              cancelContestedHoursRequest({
                associationId: work.id ?? 0,
              })
            }
            style={{ padding: '0 0.5rem' }}
            disabled={cancelContestedHoursRequestMutationState.isPending}
            loading={cancelContestedHoursRequestMutationState.isPending}
            danger
          >
            <Text fontSize="12px">Cancel contest</Text>
          </ButtonWithConfirmation>
        ) : null}
        {canContestHours ? (
          <CreateContestedHoursRequestButton
            associationId={work.id}
            workerName={work?.worker?.name ?? 'N/A'}
            shiftStartsAt={shiftDetail.startsAt}
            shiftEndsAt={shiftDetail.endsAt}
            startedAt={work.startedAt}
            finishedAt={work.completedAt}
            initialRequestedStartedAt={work.startedAt}
            initialRequestedFinishedAt={work.completedAt}
            initialRequestedBreakDuration={initialRequestedBreakDuration}
          />
        ) : null}
        <ButtonWithConfirmation
          message="Are you sure you want to refresh this worker's work item in Dayforce? Any changes might take a few minutes to reflect in the system."
          variant="secondary"
          kind="small"
          onClick={() =>
            scheduleWorkItemUpdate({
              associationId: work.id ?? 0,
              shiftId: shiftDetail.id,
            })
          }
          style={{ padding: '0 0.5rem' }}
          disabled={scheduleWorkItemUpdateMutationState.isPending}
          loading={scheduleWorkItemUpdateMutationState.isPending}
        >
          <Text fontSize="12px">Sync systems</Text>
        </ButtonWithConfirmation>
      </Flex>
    </StyledInfoTable>
  )
}
